/**
 * Sim state → RenderFrame. Pure functions: interpolation between the previous and the
 * current tick, telegraph fill progress, and SimEvent → one-shot FX mapping.
 */
import type { ContentRegistry, DeepReadonly, School, SimEvent, UnitId, World } from '@keystone/sim';
import { AUTO_SHOT_ABILITY_ID, MELEE_ABILITY_ID, TICK_MS, jumpHeightAt } from '@keystone/sim';

import type { AnimCue, FxKind, RenderArea, RenderFrame, RenderFx, RenderUnit } from '../render/types';

/** +autoattack: cue id of an off-hand white swing (render/toon/anim/kits/autoattack.ts OFFHAND_CLIP_ID). */
export const OFFHAND_CUE_ID = 'melee.oh';

/** Position/facing of every unit at the start of a tick (cheap alternative to a snapshot). */
export type PoseMap = Map<UnitId, { x: number; y: number; facing: number; z?: number }>;

export function capturePoses(world: DeepReadonly<World>): PoseMap {
  const m: PoseMap = new Map();
  for (const id of world.unitOrder) {
    const u = world.units[id];
    if (!u) continue;
    const z = u.jump?.air?.z ?? u.perch?.z; // +glide
    m.set(id, z === undefined ? { x: u.pos.x, y: u.pos.y, facing: u.facing } : { x: u.pos.x, y: u.pos.y, facing: u.facing, z });
  }
  return m;
}

export const lerp = (a: number, b: number, t: number): number => a + (b - a) * t;

/** Interpolate angles along the short arc. */
export function lerpAngle(a: number, b: number, t: number): number {
  const d = Math.atan2(Math.sin(b - a), Math.cos(b - a));
  return a + d * t;
}

export interface FrameView {
  playerId: UnitId | null;
  selectedId: UnitId | null;
  hoverId: UnitId | null;
  /** Units that cannot act (stun/incap/disorient), sampled once per tick. */
  ccIds?: ReadonlySet<UnitId>;
  /** +basekits: stealthed heroes (Lurk), drawn faded. */
  stealthIds?: ReadonlySet<UnitId>;
  /** Ability school lookup for cast glows. */
  schoolOf?: (abilityId: string) => School | null;
}

export function buildFrame(world: DeepReadonly<World>, prev: PoseMap, alpha: number, view: FrameView, newFx: RenderFx[] = []): RenderFrame {
  const a = Math.min(1, Math.max(0, alpha));
  // Interpolated render time sits between the previous tick and the current one.
  const timeMs = world.time - TICK_MS + a * TICK_MS;
  const party = new Set(world.partyIds);
  const units: RenderUnit[] = [];
  for (const id of world.unitOrder) {
    const u = world.units[id];
    if (!u || u.departed) continue; // +personas: a member who left the group is gone from the world
    const p = prev.get(id);
    // Big jumps (respawn, teleport) snap instead of sliding across the map.
    const snap = !p || Math.hypot(u.pos.x - p.x, u.pos.y - p.y) > 15;
    units.push({
      id,
      name: u.name,
      kind: u.kind,
      faction: u.faction,
      role: u.role,
      tier: u.tier,
      pos: snap ? { x: u.pos.x, y: u.pos.y } : { x: lerp(p.x, u.pos.x, a), y: lerp(p.y, u.pos.y, a) },
      facing: snap ? u.facing : lerpAngle(p.facing, u.facing, a),
      radius: u.radius,
      alive: u.alive,
      hpFrac: u.maxHp > 0 ? Math.max(0, Math.min(1, u.hp / u.maxHp)) : 0,
      color: u.color,
      isPlayer: id === view.playerId,
      casting: !!u.cast,
      classId: u.classId,
      specId: u.specId ?? null, // +specs
      npcId: u.npcId,
      speed: p && !snap ? Math.hypot(u.pos.x - p.x, u.pos.y - p.y) / (TICK_MS / 1000) : 0,
      vel: p && !snap ? { x: (u.pos.x - p.x) / (TICK_MS / 1000), y: (u.pos.y - p.y) / (TICK_MS / 1000) } : { x: 0, y: 0 },
      inCombat: u.inCombat,
      cast: gatherCast(world, id, timeMs) ?? (u.cast
        ? {
            kind: u.cast.kind,
            school: view.schoolOf?.(u.cast.abilityId) ?? null,
            progress: clamp01((timeMs - u.cast.startedAt) / Math.max(1, u.cast.endsAt - u.cast.startedAt)),
            abilityId: u.cast.abilityId,
          }
        : null), // +prof: a gather channel plays the kneel / pickaxe loop
      cc: view.ccIds?.has(id) ?? false,
      ...(view.stealthIds?.has(id) ? { stealth: true } : {}), // +basekits
      ...(u.jump && !u.jump.air ? { jumpH: jumpHeightAt(u.jump, timeMs) } : {}), // +jump
      ...airOf(u, p, a, snap), // +glide: free flight / a platform: the absolute height
      ...(u.elite ? { elite: true } : {}), // +field (renderer: elite size + gold ring)
      ...(u.level !== undefined ? { level: u.level } : {}), // +field
    });
  }
  const isHostile = (sourceId: UnitId): boolean => !party.has(sourceId) && world.units[sourceId]?.faction !== 'party';
  const areas: RenderArea[] = [];
  for (const t of world.telegraphs) {
    const total = Math.max(1, t.resolvesAt - t.createdAt);
    const ra: RenderArea = { id: t.id, kind: 'telegraph', area: t.area as RenderArea['area'], progress: clamp01((timeMs - t.createdAt) / total), hostile: isHostile(t.sourceId), abilityId: t.abilityId };
    if (t.intent) ra.intent = t.intent; // +encounters
    areas.push(ra);
  }
  for (const z of world.zones) {
    const total = Math.max(1, z.expiresAt - z.createdAt);
    const ra: RenderArea = { id: z.id, kind: 'zone', area: z.area as RenderArea['area'], progress: clamp01((z.expiresAt - timeMs) / total), hostile: isHostile(z.sourceId), abilityId: z.abilityId };
    if (z.intent) ra.intent = z.intent; // +encounters
    if (z.motion) ra.motion = z.motion.spec.type;
    areas.push(ra);
  }
  const frame: RenderFrame = { timeMs, units, areas, newFx, playerId: view.playerId, selectedId: view.selectedId, hoverId: view.hoverId };
  if (world.field?.gather) frame.gather = world.field.gather; // +prof
  if (world.worldObjects) frame.world = { objects: world.worldObjects, pickups: world.pickups ?? [], looterId: world.worldLoot?.looterId ?? null }; // +world-resources
  return frame;
}

const clamp01 = (v: number): number => Math.min(1, Math.max(0, v));

/** +glide: a flyer's / percher's absolute height (interpolated from the last tick) and the glider. */
function airOf(u: DeepReadonly<World['units'][string]>, p: { z?: number } | undefined, a: number, snap: boolean): Pick<RenderUnit, 'z' | 'glide' | 'perched'> {
  const air = u.jump?.air;
  const z = air?.z ?? u.perch?.z;
  if (z === undefined) return {};
  const z0 = !snap && p?.z !== undefined ? p.z : z;
  return { z: lerp(z0, z, a), ...(air?.glide ? { glide: true } : {}), ...(u.perch && !air ? { perched: true } : {}) };
}

/** Abilities whose `cast` should fly a projectile (ranged, targeted). */
function isRangedTargeted(content: ContentRegistry, abilityId: string): boolean {
  const a = content.ability(abilityId);
  return !!a && a.targeting === 'enemy' && !a.melee && (a.range ?? 0) > 8;
}

/** Convert one tick's events into FX. `nextId` provides stable ids. */
export function eventsToFx(events: readonly SimEvent[], content: ContentRegistry, startMs: number, nextId: () => number): RenderFx[] {
  const out: RenderFx[] = [];
  const push = (kind: FxKind, e: { sourceID?: string; targetID?: string }, durationMs: number, extra: Partial<RenderFx> = {}): void => {
    out.push({ id: nextId(), kind, sourceId: e.sourceID ?? null, targetId: e.targetID ?? null, school: null, startMs, durationMs, ...extra });
  };
  for (const e of events) {
    switch (e.type) {
      case 'cast':
        if (e.targetID && e.targetID !== e.sourceID && isRangedTargeted(content, e.abilityGameID)) {
          push('projectile', e, 280, { school: content.ability(e.abilityGameID)?.school ?? null });
        }
        break;
      case 'damage':
        if (e.hitType === 'miss' || e.hitType === 'dodge' || e.hitType === 'parry' || e.hitType === 'immune') break;
        push(e.hitType === 'crit' ? 'crit' : 'hit', e, e.hitType === 'crit' ? 380 : 240, { school: e.school, amount: e.amount + e.absorbed });
        break;
      case 'heal':
        if (e.amount > 0) push('heal', e, 400, { amount: e.amount });
        break;
      case 'interrupt':
        push('interrupt', e, 500);
        break;
      case 'death':
        push('death', e, 700);
        break;
    }
  }
  return out;
}

/** Animation for an ability's cast moment: weapon swing for melee, spell gesture otherwise. */
export function castCueKind(content: ContentRegistry, abilityId: string): 'swing' | 'spell' {
  const a = content.ability(abilityId);
  return a?.melee || (a && a.targeting === 'enemy' && !a.range) ? 'swing' : 'spell';
}

/** Convert one tick's events into animation cues (swings, instant abilities, hit reacts). */
export function eventsToCues(events: readonly SimEvent[], content: ContentRegistry): AnimCue[] {
  const out: AnimCue[] = [];
  for (const e of events) {
    if (e.type === 'damage') {
      // +autoattack: off-hand swings alternate with the main hand; Auto Shot draws and looses.
      if (e.abilityGameID === MELEE_ABILITY_ID) out.push({ unitId: e.sourceID, kind: 'swing', abilityId: e.swing === 'oh' ? OFFHAND_CUE_ID : MELEE_ABILITY_ID });
      else if (e.abilityGameID === AUTO_SHOT_ABILITY_ID) out.push({ unitId: e.sourceID, kind: 'spell', abilityId: AUTO_SHOT_ABILITY_ID });
      if (!e.tick && e.amount + e.absorbed > 0 && e.sourceID !== e.targetID) out.push({ unitId: e.targetID, kind: 'hit' });
    } else if (e.type === 'cast') {
      out.push({ unitId: e.sourceID, kind: castCueKind(content, e.abilityGameID), abilityId: e.abilityGameID });
    } else if (e.type === 'jump') {
      out.push({ unitId: e.sourceID, kind: 'spell', abilityId: 'common.jump' }); // +jump: render/toon/anim/kits/jump.ts
    }
  }
  return out;
}

/** +prof: a hero channelling a gather draws as a channel of the herb / mine clip. */
function gatherCast(world: DeepReadonly<World>, id: string, timeMs: number): RenderUnit['cast'] | undefined {
  const ch = world.field?.gather?.channels[id];
  if (!ch) return undefined;
  const ore = /_(seam|vein|lode)_|^rustvein/.test(ch.nodeId);
  return { kind: 'channel', school: null, progress: Math.max(0, Math.min(1, (timeMs - ch.startAt) / Math.max(1, ch.endAt - ch.startAt))), abilityId: ore ? 'gather.mine' : 'gather.herb' };
}
